import { createContext, useContext, useState, useEffect } from "react";

/**
 * Preferenza globale di tema (Scuro / Chiaro).
 * - "dark": nessuna classe sull'<html>, i token restano quelli di `:root`.
 *   E' il default storico dell'app e deve restare identico a com'era prima
 *   che il tema chiaro esistesse.
 * - "light": classe `theme-light` sull'<html>; tutto il CSS del tema chiaro
 *   vive sotto quella classe, cosi' il tema scuro non puo' regredire.
 * Persistita in localStorage, come lingua e dimensione testo.
 *
 * L'applicazione della classe al primo paint NON avviene qui: la fa uno
 * script inline in `public/index.html`, altrimenti chi ha scelto il chiaro
 * vedrebbe un lampo scuro ad ogni apertura (il bundle arriva dopo).
 */
const ThemeContext = createContext(null);

const VALID = ["dark", "light"];
const DEFAULT_THEME = "dark";
const LIGHT_CLASS = "theme-light";
const STORAGE_KEY = "theme";

/* Colore della barra di stato su mobile / PWA. Nel tema scuro resta il volt
   storico; nel chiaro va sul fondo chiaro, altrimenti la barra verde stacca
   da una UI chiara. */
const THEME_COLOR = {
	dark: "#00D68F",
	light: "#FAFAFA",
};

function readInitialTheme() {
	try {
		const saved = localStorage.getItem(STORAGE_KEY);
		return VALID.includes(saved) ? saved : DEFAULT_THEME;
	} catch {
		return DEFAULT_THEME;
	}
}

export function ThemeProvider({ children }) {
	const [theme, setThemeState] = useState(readInitialTheme);

	useEffect(() => {
		try {
			localStorage.setItem(STORAGE_KEY, theme);
		} catch {
			/* storage pieno o bloccato: la preferenza vale solo per la sessione */
		}

		const root = document.documentElement;
		root.classList.toggle(LIGHT_CLASS, theme === "light");

		const meta = document.querySelector('meta[name="theme-color"]');
		if (meta) meta.setAttribute("content", THEME_COLOR[theme]);
	}, [theme]);

	const setTheme = (value) => {
		if (VALID.includes(value)) setThemeState(value);
	};

	const toggleTheme = () => {
		setThemeState((prev) => (prev === "dark" ? "light" : "dark"));
	};

	return (
		<ThemeContext.Provider value={{ theme, setTheme, toggleTheme }}>
			{children}
		</ThemeContext.Provider>
	);
}

export function useTheme() {
	const context = useContext(ThemeContext);
	if (!context) {
		throw new Error("useTheme must be used within ThemeProvider");
	}
	return context;
}
